import { ChatMessage } from '../../../shared/CharacterTypes';

/**
 * ChatHandler - Manages chat system functionality
 * Singleton pattern for global chat state management
 */
export class ChatHandler {
  private static instance: ChatHandler | null = null;
  private messages: ChatMessage[] = [];
  private listeners: Set<(messages: ChatMessage[]) => void> = new Set();
  private maxMessages: number = 100;

  private constructor() {
    this.messages = [];
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ChatHandler {
    if (!ChatHandler.instance) {
      ChatHandler.instance = new ChatHandler();
    }
    return ChatHandler.instance;
  }

  /**
   * Send a chat message
   */
  sendMessage(text: string, sender: string = 'System', type: ChatMessage['type'] = 'global'): void {
    const message: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      sender,
      text,
      timestamp: Date.now(),
      type
    };

    this.addMessage(message);
  }

  /**
   * Add a message to chat
   */
  addMessage(message: ChatMessage): void {
    this.messages = [...this.messages, message];

    // Trim to max messages
    if (this.messages.length > this.maxMessages) {
      this.messages = this.messages.slice(-this.maxMessages);
    }

    this.notifyListeners();
  }

  /**
   * Add multiple messages
   */
  addMessages(messages: ChatMessage[]): void {
    this.messages = [...this.messages, ...messages];

    // Trim to max messages
    if (this.messages.length > this.maxMessages) {
      this.messages = this.messages.slice(-this.maxMessages);
    }

    this.notifyListeners();
  }

  /**
   * Clear all messages
   */
  clear(): void {
    this.messages = [];
    this.notifyListeners();
  }

  /**
   * Get all messages
   */
  getMessages(): ChatMessage[] {
    return [...this.messages];
  }

  /**
   * Get recent messages
   */
  getRecentMessages(count: number): ChatMessage[] {
    return this.messages.slice(-count);
  }

  /**
   * Subscribe to message updates
   */
  subscribe(listener: (messages: ChatMessage[]) => void): () => void {
    this.listeners.add(listener);

    // Immediately notify with current messages
    listener(this.messages);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.messages));
  }

  /**
   * Set max message history
   */
  setMaxMessages(max: number): void {
    this.maxMessages = max;

    // Trim if necessary
    if (this.messages.length > max) {
      this.messages = this.messages.slice(-max);
      this.notifyListeners();
    }
  }

  /**
   * Create system message
   */
  systemMessage(text: string): void {
    this.sendMessage(text, 'System', 'system');
  }

  /**
   * Create error message
   */
  errorMessage(text: string): void {
    this.sendMessage(`ERROR: ${text}`, 'System', 'system');
  }

  /**
   * Create info message
   */
  infoMessage(text: string): void {
    this.sendMessage(`INFO: ${text}`, 'System', 'system');
  }

  /**
   * Create debug message (goes to the Debug tab)
   */
  debugMessage(text: string, sender: string = 'System'): void {
    this.sendMessage(text, sender, 'debug');
  }

  /**
   * Create combat message (goes to the Combat tab)
   */
  combatMessage(text: string): void {
    this.sendMessage(text, 'Combat', 'combat');
  }
}
